// app/components/Plugins/OptionsBuilder/OptionsBuilder.tsx
'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import type { Leg } from './types'
import styles from './OptionsBuilder.module.css'

/**
 * @uiComponent
 * @name OptionsBuilder
 * @description The OptionsBuilder component provides a comprehensive options strategy construction and analysis tool.
 * It allows users to build complex multi-leg options strategies, visualize payoff diagrams, and analyze risk/reward profiles.
 * Features include leg management, real-time payoff calculations, breakeven analysis, and interactive charting capabilities.
 * @location OptionsBuilder plugin in the trading terminal
 * @prop none - No external props, manages internal state for strategy construction and analysis
 */

/**
 * @uiWorkflow
 * @name Build Multi-Leg Options Strategy
 * @description How to construct and analyze complex options strategies with multiple legs
 * @platform optionsbuilder
 * @step 1 Navigate to the Options Builder plugin [component: OptionsBuilder] [action: navigate]
 * @step 2 Review the default strategy legs [component: OptionsBuilder] [action: observe] [notes: Pre-loaded with sample ZOMATO strategy]
 * @step 3 Modify existing legs if needed [component: OptionsBuilder] [action: edit] [notes: Click on leg values to edit]
 * @step 4 Add a new leg to the strategy [component: OptionsBuilder] [action: create]
 * @step 5 Set the strike price for the new leg [component: OptionsBuilder] [action: input] [notes: Enter target strike price]
 * @step 6 Choose option type (CALL or PUT) [component: OptionsBuilder] [action: select]
 * @step 7 Select position type (BUY or SELL) [component: OptionsBuilder] [action: choose]
 * @step 8 Enter the premium price [component: OptionsBuilder] [action: input] [notes: Current market premium]
 * @step 9 Set the quantity for the leg [component: OptionsBuilder] [action: specify]
 * @step 10 Click "Add Leg" to include in strategy [component: OptionsBuilder] [action: add]
 * @step 11 Click "Build Strategy" to generate analysis [component: OptionsBuilder] [action: analyze]
 * @step 12 Review payoff chart and risk metrics [component: OptionsBuilder] [action: examine]
 */

/**
 * @uiWorkflow
 * @name Analyze Strategy Risk and Reward Profile
 * @description How to evaluate the financial characteristics of your options strategy
 * @platform optionsbuilder
 * @step 1 Complete strategy construction [component: OptionsBuilder] [action: prerequisite] [notes: Build strategy first]
 * @step 2 Examine the payoff chart visualization [component: OptionsBuilder] [action: analyze] [notes: Shows profit/loss at different stock prices]
 * @step 3 Review maximum profit potential [component: OptionsBuilder] [action: observe] [notes: May show "Unlimited" for some strategies]
 * @step 4 Check maximum loss exposure [component: OptionsBuilder] [action: assess] [notes: Important for risk management]
 * @step 5 Identify breakeven points [component: OptionsBuilder] [action: locate] [notes: Stock prices where strategy breaks even]
 * @step 6 Analyze profit zones on the chart [component: OptionsBuilder] [action: identify] [notes: Price ranges where strategy is profitable]
 * @step 7 Consider time decay effects [component: OptionsBuilder] [action: evaluate] [notes: How theta affects the strategy]
 * @step 8 Adjust strategy if needed [component: OptionsBuilder] [action: optimize] [notes: Modify legs based on analysis]
 */

/**
 * @uiWorkflow
 * @name Manage and Modify Strategy Legs
 * @description How to add, edit, and remove legs from your options strategy
 * @platform optionsbuilder
 * @step 1 Access the strategy legs table [component: OptionsBuilder] [action: locate]
 * @step 2 Review current legs in the basket [component: OptionsBuilder] [action: examine] [notes: Shows all strategy components]
 * @step 3 Click "Remove" to delete unwanted legs [component: OptionsBuilder] [action: delete]
 * @step 4 Modify leg parameters as needed [component: OptionsBuilder] [action: edit] [notes: Strike, premium, quantity]
 * @step 5 Add protective legs for risk management [component: OptionsBuilder] [action: hedge]
 * @step 6 Test different strike combinations [component: OptionsBuilder] [action: experiment]
 * @step 7 Adjust quantities for position sizing [component: OptionsBuilder] [action: scale]
 * @step 8 Update spot price for current market [component: OptionsBuilder] [action: refresh] [notes: Affects payoff calculations]
 * @step 9 Rebuild strategy after changes [component: OptionsBuilder] [action: recalculate]
 * @step 10 Compare before and after analysis [component: OptionsBuilder] [action: compare]
 */

// Dynamically import the PayoffChart with SSR disabled
const PayoffChart = dynamic(() => import('./components/PayoffChart'), {
  ssr: false
})

// Define the type for the analysis result (note: maxProfit and maxLoss may be numbers or the string "Unlimited")
interface AnalysisResult {
  maxProfit: number | string
  maxLoss: number | string
  breakEvens: number[]
}

// Define the type for the API response from the options builder
interface BuilderResult {
  payoffPoints: { stockPrice: number; payoff: number }[]
  analysis: AnalysisResult
}

export default function OptionsBuilderPage() {
  // --- State for the basket of legs ---
  const [basket, setBasket] = useState<Leg[]>([
    {
      symbol: 'ZOMATO',
      expiry: '27 Feb',
      strike: 225,
      optionType: 'PUT',
      positionType: 'BUY',
      premium: 4.9,
      quantity: 2000
    },
    {
      symbol: 'ZOMATO',
      expiry: '27 Feb',
      strike: 240,
      optionType: 'CALL',
      positionType: 'BUY',
      premium: 9.65,
      quantity: 4000
    },
    {
      symbol: 'ZOMATO',
      expiry: '27 Feb',
      strike: 285,
      optionType: 'CALL',
      positionType: 'SELL',
      premium: 0.95,
      quantity: 2000
    }
  ])

  // --- State for the current spot price ---
  const [spotPrice, setSpotPrice] = useState(238.2)

  // --- State for the new leg form ---
  // We no longer require the user to input symbol and expiry.
  // (We assign default placeholder values; the API route will use these if needed.)
  const [newLeg, setNewLeg] = useState<Leg>({
    symbol: 'DEFAULT',
    expiry: 'DEFAULT',
    strike: 0,
    optionType: 'CALL',
    positionType: 'BUY',
    premium: 0,
    quantity: 0
  })

  // --- State for the result returned from the API ---
  const [builderResult, setBuilderResult] = useState<BuilderResult | null>(null)
  // --- Loading state ---
  const [loading, setLoading] = useState(false)

  // Handler to update new leg fields (for numeric and select fields)
  const handleNewLegChange = (field: keyof Leg, value: string | number) => {
    setNewLeg(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Handler to add a new leg to the basket.
  // (No validation is needed for symbol/expiry since they’re not being input by the user.)
  const handleAddLeg = () => {
    setBasket(prev => [...prev, newLeg])
    // Reset the new leg form to defaults.
    setNewLeg({
      symbol: 'DEFAULT',
      expiry: 'DEFAULT',
      strike: 0,
      optionType: 'CALL',
      positionType: 'BUY',
      premium: 0,
      quantity: 0
    })
  }

  // Handler to remove a leg from the basket by index.
  const handleRemoveLeg = (index: number) => {
    setBasket(prev => prev.filter((_, i) => i !== index))
  }

  // Handler for the Run Builder button:
  // Sends the basket and spotPrice to the API route.
  const handleRunBuilder = async () => {
    setLoading(true)
    setBuilderResult(null)
    try {
      const res = await fetch('/api/optionsbuilder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ basket, spotPrice })
      })
      if (!res.ok) {
        throw new Error('Failed to compute options analysis.')
      }
      const data: BuilderResult = await res.json()
      setBuilderResult(data)
    } catch (error: any) {
      console.error('Error running builder:', error)
      alert('Error: ' + error.message)
    }
    setLoading(false)
  }

  // Format analysis texts.
  const maxProfitValue = builderResult?.analysis?.maxProfit
  const maxLossValue = builderResult?.analysis?.maxLoss
  const breakEvens = builderResult?.analysis?.breakEvens

  const maxProfitText = builderResult
    ? typeof maxProfitValue === 'number'
      ? maxProfitValue === Infinity
        ? 'Unlimited'
        : maxProfitValue.toFixed(2)
      : maxProfitValue
    : ''

  const maxLossText = builderResult
    ? typeof maxLossValue === 'number'
      ? maxLossValue === -Infinity
        ? 'Unlimited'
        : maxLossValue.toFixed(2)
      : maxLossValue
    : ''

  const breakEvenText =
    builderResult && Array.isArray(breakEvens)
      ? breakEvens.map((be: number) => be.toFixed(2)).join(', ')
      : ''

  return (
    <div className={styles.container}>
      <h1 className={styles.header}>Options Builder</h1>

      {/* Spot Price input */}
      <label className={styles.label}>
        Spot Price:
        <input
          className={styles.input}
          type="number"
          value={spotPrice}
          onChange={e => setSpotPrice(parseFloat(e.target.value))}
        />
      </label>

      {/* New Leg form */}
      <h2 className={styles.label}>Add New Leg</h2>
      <div className={styles.newLegForm}>
        {/* Removed Symbol and Expiry inputs */}
        <input
          type="number"
          placeholder="Strike Price"
          value={newLeg.strike === 0 ? '' : newLeg.strike}
          onChange={e => handleNewLegChange('strike', Number(e.target.value))}
          className={styles.input}
        />
        <select
          value={newLeg.optionType}
          onChange={e => handleNewLegChange('optionType', e.target.value)}
          className={styles.input}
        >
          <option value="CALL">CALL</option>
          <option value="PUT">PUT</option>
        </select>
        <select
          value={newLeg.positionType}
          onChange={e => handleNewLegChange('positionType', e.target.value)}
          className={styles.input}
        >
          <option value="BUY">BUY</option>
          <option value="SELL">SELL</option>
        </select>
        <input
          type="number"
          placeholder="Premium"
          value={newLeg.premium === 0 ? '' : newLeg.premium}
          onChange={e => handleNewLegChange('premium', Number(e.target.value))}
          className={styles.input}
        />
        <input
          type="number"
          placeholder="Quantity"
          value={newLeg.quantity === 0 ? '' : newLeg.quantity}
          onChange={e => handleNewLegChange('quantity', Number(e.target.value))}
          className={styles.input}
        />
        <button onClick={handleAddLeg} className={styles.buttonLeg}>
          Add Leg
        </button>
      </div>

      {/* List current basket legs with a remove (X) button */}
      <h2 className={styles.label}>Basket Legs</h2>
      <ul className={styles.list}>
        {basket.map((leg, i) => (
          <li key={i} className={styles.listItem}>
            <span>
              {leg.positionType} {leg.optionType} {leg.strike} @ {leg.premium} x{' '}
              {leg.quantity}
            </span>
            <button
              onClick={() => handleRemoveLeg(i)}
              className={styles.removeButton}
            >
              X
            </button>
          </li>
        ))}
      </ul>

      {/* Run Builder button */}
      <button
        onClick={handleRunBuilder}
        disabled={loading}
        className={styles.button}
      >
        {loading ? 'Running...' : 'Run Builder'}
      </button>

      {/* Display results from the API call */}
      {builderResult && (
        <>
          <h2>Payoff Chart</h2>
          <PayoffChart
            data={builderResult.payoffPoints}
            spotPrice={spotPrice}
          />

          <h2>Analysis</h2>
          <p>Max Profit: {maxProfitText}</p>
          <p>Max Loss: {maxLossText}</p>
          <p>Break‐Evens: {breakEvenText}</p>
        </>
      )}
    </div>
  )
}
